import type {
  HumanMessage,
  UserMessage,
} from "@redotech/merchant-sdk/rpc/schema/agents/merchant-support-agent/escalation";
import type { ThreadEscalationInfo } from "@redotech/merchant-sdk/rpc/schema/agents/merchant-support-agent/message-merchant-support-agent";
import { useHandler } from "@redotech/react-util/hook";
import { useStore } from "@redotech/react-util/store";
import { showNotification } from "@redotech/redo-web/alert";
import { dismissNotificationByKey } from "@redotech/redo-web/notification-provider";
import { localStore } from "@redotech/web-util/storage";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import type {
  EscalationContextType,
  EscalationStatus,
} from "./escalation-context";
import { sanitizeIntercomBody } from "./use-human-message-handler";

const ESCALATION_NOTIFICATION_KEY = "help-chat-escalation-human-message";
const ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH = 140;
const dismissedEscalationStore = localStore(
  "redo.escalation-notification-dismissed",
);

export function clearEscalationNotification() {
  dismissNotificationByKey(ESCALATION_NOTIFICATION_KEY);
}

interface EscalationThread {
  _id: string;
  escalation?: ThreadEscalationInfo;
}

interface UseEscalationNotificationsParams<T extends EscalationThread> {
  inChatEscalation: boolean;
  isOpen: boolean;
  open: () => void;
  threads: ReadonlyArray<T>;
  escalationContext: EscalationContextType;
  merchantRpcClient: {
    checkActiveEscalation: (params: {}) => Promise<{
      activeEscalation: {
        escalationId: string;
        triageThreadId: string;
        status: EscalationStatus;
      } | null;
    }>;
    getEscalationMessages: (params: {
      escalationId: string;
    }) => Promise<{
      status: string;
      messages: HumanMessage[];
      userMessages: UserMessage[];
    }>;
    getMerchantSupportAgentThreads: (params: {
      userEmail: string;
    }) => Promise<{ threads: T[] }>;
  };
  loadThreadIntoChat: (threadId: string, thread: T) => Promise<void>;
  userEmail: string | undefined;
  hasInitializedRef: MutableRefObject<boolean>;
  pendingThreadLoadInFlightRef: MutableRefObject<boolean>;
}

export function useEscalationNotifications<T extends EscalationThread>({
  inChatEscalation,
  isOpen,
  open,
  threads,
  escalationContext,
  merchantRpcClient,
  loadThreadIntoChat,
  userEmail,
  hasInitializedRef,
  pendingThreadLoadInFlightRef,
}: UseEscalationNotificationsParams<T>) {
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [pendingThreadToOpen, setPendingThreadToOpen] = useState<string | null>(
    null,
  );
  const hasRecoveredEscalationRef = useRef(false);
  const [dismissedEscalationId, setDismissedEscalationId] = useStore(
    dismissedEscalationStore,
  );

  const { setEscalation, setTriageThreadId } = escalationContext;

  const openEscalatedThreadFromNotification = useHandler((threadId: string) => {
    hasInitializedRef.current = true;
    clearEscalationNotification();
    setIsLoadingThread(true);
    setPendingThreadToOpen(threadId);
    open();
  });

  const notifyClosedEscalationMessage = useHandler((message: HumanMessage) => {
    if (!inChatEscalation) return;

    setDismissedEscalationId(undefined);

    const threadId = escalationContext.triageThreadId;
    if (!threadId) return;

    const singleLineMessage = sanitizeIntercomBody(message.body);
    const description =
      singleLineMessage.length > 0
        ? singleLineMessage.length > ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH
          ? `${singleLineMessage.slice(0, ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH - 1)}...`
          : singleLineMessage
        : "Support sent a new message";

    const escalationId = escalationContext.escalation.escalationId;

    dismissNotificationByKey(ESCALATION_NOTIFICATION_KEY);
    showNotification({
      notificationKey: ESCALATION_NOTIFICATION_KEY,
      title: `${message.authorName?.split(" ")[0] || "Support"} sent you a message`,
      description,
      primaryButton: {
        text: "Open chat",
        onClick: () => openEscalatedThreadFromNotification(threadId),
      },
      onDismiss: () => {
        if (escalationId) {
          setDismissedEscalationId(escalationId);
        }
      },
    });
  });

  // Recover escalation state when panel is closed and context was cleared.
  // Runs on page load and re-runs after panel close to keep SSE connected.
  // Verifies with the server before restoring context to avoid stale notifications.
  useEffect(() => {
    if (!inChatEscalation) return;
    if (isOpen) return;
    if (threads.length === 0) return;
    if (escalationContext.escalation.escalationId) return;

    let cancelled = false;

    void (async () => {
      try {
        const { activeEscalation } =
          await merchantRpcClient.checkActiveEscalation({});

        if (cancelled || !activeEscalation) return;

        const activeThread = threads.find(
          (t) => t._id === activeEscalation.triageThreadId,
        );

        if (!activeThread?.escalation) return;

        const escalation = activeThread.escalation;

        // Restore escalation context — triggers SSE reconnection.
        // Use fresh escalationId from server, not the cached thread snapshot.
        setTriageThreadId(activeThread._id);
        setEscalation({
          escalationId: activeEscalation.escalationId,
          noticeTopic: escalation.noticeTopic,
          status: activeEscalation.status,
          intercomConversationId: null,
        });

        // Show notification only on first recovery per page load
        if (
          !hasRecoveredEscalationRef.current &&
          activeEscalation.status === "human-active" &&
          dismissedEscalationId !== activeEscalation.escalationId
        ) {
          try {
            const result = await merchantRpcClient.getEscalationMessages({
              escalationId: activeEscalation.escalationId,
            });

            if (cancelled) return;

            const lastMessage =
              result.messages.length > 0
                ? result.messages.reduce((latest, msg) =>
                    msg.createdAt > latest.createdAt ? msg : latest,
                  )
                : undefined;

            const authorName =
              lastMessage?.authorName?.split(" ")[0] || "Support";
            const body = lastMessage
              ? sanitizeIntercomBody(lastMessage.body)
              : "";
            const description =
              body.length > ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH
                ? `${body.slice(0, ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH - 1)}...`
                : body || "Sent you a message";

            showNotification({
              notificationKey: ESCALATION_NOTIFICATION_KEY,
              title: `${authorName} sent you a message`,
              description,
              primaryButton: {
                text: "Open chat",
                onClick: () =>
                  openEscalatedThreadFromNotification(activeThread._id),
              },
              onDismiss: () => {
                setDismissedEscalationId(escalation.escalationId);
              },
            });
          } catch {
            if (cancelled) return;

            showNotification({
              notificationKey: ESCALATION_NOTIFICATION_KEY,
              title: "Support agent responded",
              description: "A support agent has replied to your escalation",
              primaryButton: {
                text: "Open chat",
                onClick: () =>
                  openEscalatedThreadFromNotification(activeThread._id),
              },
              onDismiss: () => {
                setDismissedEscalationId(escalation.escalationId);
              },
            });
          }
        }
        hasRecoveredEscalationRef.current = true;
      } catch {
        // Server check failed — don't restore stale context
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    threads,
    escalationContext.escalation.escalationId,
    setTriageThreadId,
    setEscalation,
    dismissedEscalationId,
    openEscalatedThreadFromNotification,
    setDismissedEscalationId,
    merchantRpcClient,
    inChatEscalation,
  ]);

  // Handle pending thread open (from notification click)
  useEffect(() => {
    if (!isOpen || !pendingThreadToOpen) return;
    if (pendingThreadLoadInFlightRef.current) return;

    const loadPendingThread = async () => {
      pendingThreadLoadInFlightRef.current = true;
      if (!userEmail) {
        setPendingThreadToOpen(null);
        pendingThreadLoadInFlightRef.current = false;
        return;
      }
      try {
        const cachedThread = threads.find((t) => t._id === pendingThreadToOpen);
        if (cachedThread) {
          await loadThreadIntoChat(pendingThreadToOpen, cachedThread);
          return;
        }

        const latestThreads =
          await merchantRpcClient.getMerchantSupportAgentThreads({ userEmail });
        const freshThread = latestThreads.threads.find(
          (t) => t._id === pendingThreadToOpen,
        );
        if (freshThread) {
          await loadThreadIntoChat(pendingThreadToOpen, freshThread);
        } else {
          setIsLoadingThread(false);
        }
      } finally {
        setPendingThreadToOpen(null);
        pendingThreadLoadInFlightRef.current = false;
      }
    };

    void loadPendingThread();
  }, [
    isOpen,
    pendingThreadToOpen,
    threads,
    loadThreadIntoChat,
    merchantRpcClient,
    userEmail,
    pendingThreadLoadInFlightRef,
  ]);

  // Clear notifications when panel opens
  useEffect(() => {
    if (isOpen) {
      clearEscalationNotification();
    }
  }, [isOpen]);

  return {
    isLoadingThread,
    setIsLoadingThread,
    openEscalatedThreadFromNotification,
    notifyClosedEscalationMessage,
    dismissedEscalationId,
    setDismissedEscalationId,
  };
}
